import { useQuery } from '@tanstack/react-query';
import { Mail, Send } from 'lucide-react';
import { getStudentDeviceSummary } from '../../../utils/remote';
import { useStudentParent, useSectionDetail, useStudentEngagement } from '../../api/hooks';
import type { StudentOverview } from '../../api/types';
import { Badge, Initials } from '../../components/ui';
import RecentViewersPanel from './RecentViewersPanel';
import { dayMonth, parseDate } from './lib/format';
import { formatRoll } from '../../lib/roll';

interface Props {
  studentId: number;
  overview: StudentOverview;
  isAdmin: boolean;
}

/** Artboard `.card` in the rail: a bordered white card whose title is the 17px card title. */
function RailCard({ title, actions, children }: { title: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Artboard `.cline`: an icon and a line of contact detail on a hairline rule. */
function ContactLine({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-hairline py-2 text-[13px] last:border-b-0">
      <span aria-hidden="true" className="flex-none text-muted">{icon}</span>
      <span className="min-w-0 break-all">{children}</span>
    </div>
  );
}

function Fact({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className={`m-0 min-w-0 break-words font-medium text-ink ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </>
  );
}

/** "28 Sep · 14 days" for a date in the past, "28 Sep · today" for today. */
function lastActiveLabel(iso: string | null): string | null {
  const d = parseDate(iso);
  if (!d) return null;
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(new Date()) - midnight(d)) / 86_400_000);
  const ago = days <= 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'}`;
  return `${dayMonth(iso)} · ${ago}`;
}

export default function ProfileRail({ studentId, overview, isAdmin }: Props) {
  const parent = useStudentParent(studentId);
  const section = useSectionDetail(overview.section?.id ?? null);
  const engagement = useStudentEngagement(studentId);
  // School admins only; a 404 (feature off or out of scope) resolves to null and shows nothing.
  const devices = useQuery({
    queryKey: ['student-device-summary', studentId],
    queryFn: () => getStudentDeviceSummary(studentId),
    enabled: isAdmin,
    retry: false,
    staleTime: 60_000,
  });
  const deviceSummary = isAdmin ? devices.data ?? null : null;
  const p = parent.data;
  const hasParent = !!p && !!(p.parent_name || p.parent_email);
  const { kpis, profile } = overview;
  const lastActive = lastActiveLabel(kpis.last_active_at);
  const homeroom = section.data?.homeroom_teacher;
  const homeroomName = homeroom ? (`${homeroom.first_name ?? ''} ${homeroom.last_name ?? ''}`.trim() || homeroom.username) : null;
  const exams = engagement.data?.kpis.total_exams_taken ?? null;
  const digestOpened = p?.latest_digest?.status === 'opened' && p.latest_digest.sent_at
    ? ` · last digest opened ${dayMonth(p.latest_digest.sent_at)}` : '';
  const facts: Array<{ label: string; value: string; mono?: boolean }> = [];
  if (profile.roll_number != null) facts.push({ label: 'Roll no.', value: formatRoll(profile.roll_number), mono: true });
  if (homeroomName) facts.push({ label: 'Homeroom', value: homeroomName });
  if (profile.counselor?.name) facts.push({ label: 'Counsellor', value: profile.counselor.name });
  if (exams !== null) facts.push({ label: 'Exams taken', value: String(exams), mono: true });
  if (lastActive) facts.push({ label: 'Last active', value: lastActive });

  return (
    <aside className="flex min-w-0 flex-col gap-[18px]" aria-label="Contacts and quick facts">
      <RailCard title="Parent / guardian"
        actions={hasParent && p ? <Badge tone={p.parent_digest_consent ? 'success' : 'neutral'} size="sm">{p.parent_digest_consent ? 'Digest on' : 'Digest off'}</Badge> : undefined}>
        {parent.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : parent.isError ? (
          <p role="alert" className="text-sm text-muted">Couldn't load parent contact.</p>
        ) : !hasParent || !p ? (
          <p className="text-sm text-muted">No parent contact on file.</p>
        ) : (
          <div>
            {p.parent_name && (
              <div className="mb-2 flex items-center gap-3">
                <Initials name={p.parent_name} className="!h-11 !w-11 !rounded-[14px]" />
                <span className="font-semibold text-ink">{p.parent_name}</span>
              </div>
            )}
            {p.parent_email && <ContactLine icon={<Mail size={16} strokeWidth={1.8} />}>{p.parent_email}</ContactLine>}
            <ContactLine icon={<Send size={16} strokeWidth={1.8} />}>
              {p.telegram_linked ? `Telegram linked${digestOpened}` : 'Telegram not linked'}
            </ContactLine>
          </div>
        )}
      </RailCard>

      {facts.length > 0 && (
        <RailCard title="Quick facts">
          <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-[9px] text-[13px]">
            {facts.map((f) => <Fact key={f.label} {...f} />)}
          </dl>
        </RailCard>
      )}

      {deviceSummary && (
        <RailCard title="Devices">
          <p className="m-0 text-[13px] text-ink">
            Phone changes (30 days): <span className="font-mono font-semibold">{deviceSummary.switches_30d}</span>
            {' · '}Review flags: <span className="font-mono font-semibold">{deviceSummary.open_flags}</span>
          </p>
        </RailCard>
      )}

      {isAdmin && <RecentViewersPanel studentId={studentId} />}
    </aside>
  );
}
